// =============================================
// brokenLinksSort — priority generation queue by "broken links score"
//
// Dla każdego unpublished node liczy ile distinct PUBLISHED artykułów
// linkuje do jego slug → wyższy score = wyższy priorytet wygenerowania
// (każdy unfilled link to broken `<a href>` w living published article).
//
// SRO 11 Closed Loop Enforcement: każdy broken internal link = orphan failure.
// Implementacja read-only — fetch + JS aggregation (no DB function required).
//
// Cache: 60s in-memory per mapId. Auto-refresh po batchu (TTL upłynie zanim
// user wróci do panelu). Manual invalidate przez invalidateBrokenLinksCache().
//
// Regex pattern: /href="\/([a-z0-9\-]+)"/g — kompatybilne z slugify() w
// services/schema/socialPlatforms.ts (lowercase a-z, digits, hyphens).
// Jeśli kiedyś slugi zaczną zawierać kropki/podkreślenia/Unicode → update regex.
// =============================================
import { supabase } from "@/integrations/supabase/client";

type ScoresMap = Map<string, number>;

interface CacheEntry {
  scores: ScoresMap;
  fetchedAt: number;
}

const CACHE_TTL_MS = 60_000;
// Matchuje WSZYSTKIE href (relative + absolute). Klasyfikacja "internal slug"
// w `extractInternalSlug()` — single-segment path = typowy WP postname permalink.
// FIX 2026-06-09: regex `/href="\/([a-z0-9\-]+)"/g` nie matchował absolute URLs
// po Link Integrity strip pre-publish (live target → <a href="https://domena/slug/">),
// ani polskich znaków w slugu. Skutek: brokenLinksStats.nodesAffected = 0 →
// chip "Priorytet linków" nie renderuje się w ContentEnginePanel.
const HREF_RE = /href="([^"\s]+)"/g;
// Markery <span data-pending-link="/slug">anchor</span> — po Link Integrity strip
// pre-publish, linki do unpublished targetów stają się markerami (nie <a href>).
// Te markery SĄ dokładnie tym co chip "Priorytet linków" powinien liczyć: pending
// linki czekające na publikację targetu. Bez nich chip dla mapy gdzie strip
// pre-publish był używany pokaże 0.
const PENDING_MARKER_RE = /data-pending-link="\/([^"]+)"/g;

/**
 * Wyciągnij slug (bez slash'y prefix/suffix) z dowolnego href.
 * Internal heurystyka: single-segment path (matches WP postname).
 * Zwraca null gdy multi-segment (np. /category/slug, /wiki/foo).
 */
function extractInternalSlug(rawHref: string): string | null {
  let path = "";
  if (rawHref.startsWith("/")) {
    path = rawHref;
  } else if (/^https?:\/\//.test(rawHref)) {
    try {
      path = new URL(rawHref).pathname;
    } catch {
      return null;
    }
  } else {
    return null;
  }
  // Strip leading "/", trailing "/", query/hash
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "").split("?")[0].split("#")[0];
  if (!clean) return null;
  // Single-segment guard (no nested paths)
  if (clean.includes("/")) return null;
  return clean;
}
const cache = new Map<string, CacheEntry>();

/**
 * Fetch broken_link_score per unpublished node w mapie.
 * Cache 60s per mapId — cacheKey prefix `${mapId}:broken_links_scores`
 * żeby uniknąć kolizji z innymi cache'ami w przyszłości.
 *
 * Returns Map<nodeId, score>. Nody bez broken links NIE są w mapie (treat as 0).
 */
export async function getBrokenLinksScores(mapId: string): Promise<ScoresMap> {
  const cacheKey = `${mapId}:broken_links_scores`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.scores;
  }

  const scores: ScoresMap = new Map();

  // 1. Wszystkie nody w mapie (do slug → nodeId map)
  const { data: nodes, error: nodesErr } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug")
    .eq("map_id", mapId);

  if (nodesErr || !nodes) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  // 2. Published nody w mapie (z final_html do parse'owania linków)
  // Join przez node_id → nodes filtered map_id (mamy już listę nodów z #1)
  const nodeIds = (nodes as Array<{ id: string }>).map((n) => n.id);
  if (nodeIds.length === 0) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  const { data: published, error: pubErr } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, final_html")
    .in("node_id", nodeIds)
    .eq("engine_stage", "published")
    .not("final_html", "is", null);

  if (pubErr || !published) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  // 3. Slug → nodeId map (tylko unpublished — to są targets dla scoringu)
  const publishedNodeIds = new Set(
    (published as Array<{ node_id: string }>).map((p) => p.node_id),
  );
  const slugToUnpublishedNodeId = new Map<string, string>();
  for (const n of nodes as Array<{ id: string; slug: string | null }>) {
    if (!n.slug || publishedNodeIds.has(n.id)) continue;
    slugToUnpublishedNodeId.set(n.slug, n.id);
  }

  // 4. Parse href + markery <span data-pending-link> z każdego published.final_html.
  // Markery = linki do unpublished targetów po Link Integrity strip pre-publish
  // (kluczowe dla map gdzie strip był aktywny — bez tego chip pokaże 0).
  // Within-doc dedup (jeden artykuł = jeden vote dla danego targetu).
  const slugToSources = new Map<string, Set<string>>();
  for (const p of published as Array<{ node_id: string; final_html: string }>) {
    if (!p.final_html) continue;
    const seenInThisDoc = new Set<string>();
    // Pass 1: <a href> (relative + absolute)
    for (const m of p.final_html.matchAll(HREF_RE)) {
      const targetSlug = extractInternalSlug(m[1]);
      if (!targetSlug || seenInThisDoc.has(targetSlug)) continue;
      seenInThisDoc.add(targetSlug);
      if (!slugToUnpublishedNodeId.has(targetSlug)) continue;
      const set = slugToSources.get(targetSlug) ?? new Set<string>();
      set.add(p.node_id);
      slugToSources.set(targetSlug, set);
    }
    // Pass 2: <span data-pending-link> markery (po Link Integrity strip)
    for (const m of p.final_html.matchAll(PENDING_MARKER_RE)) {
      const targetSlug = m[1].replace(/\/+$/, ""); // strip trailing slash
      if (!targetSlug || seenInThisDoc.has(targetSlug)) continue;
      seenInThisDoc.add(targetSlug);
      if (!slugToUnpublishedNodeId.has(targetSlug)) continue;
      const set = slugToSources.get(targetSlug) ?? new Set<string>();
      set.add(p.node_id);
      slugToSources.set(targetSlug, set);
    }
  }

  // 5. Aggregate → scores Map<nodeId, count>
  for (const [slug, sources] of slugToSources) {
    const targetNodeId = slugToUnpublishedNodeId.get(slug);
    if (!targetNodeId) continue;
    scores.set(targetNodeId, sources.size);
  }

  cache.set(cacheKey, { scores, fetchedAt: Date.now() });
  return scores;
}

/**
 * Manualnie invalidate cache (np. po publikacji nowego artykułu albo
 * bulk regen). Bez argumentów — clear all.
 */
export function invalidateBrokenLinksCache(mapId?: string): void {
  if (mapId) {
    cache.delete(`${mapId}:broken_links_scores`);
  } else {
    cache.clear();
  }
}
